/**
 * LA CLAVE DE GRUPO DE UN CULTIVO EN INFORMES Y PANEL
 * (05-10-2026, plan cultivos SIEX, F4 «lectores»).
 *
 * Hasta hoy cada sitio agrupaba por el NOMBRE escrito, cada uno a su manera:
 *   - informe de costes: `normName` (minúsculas, sin espacios de sobra);
 *   - campañas: `lower(trim(crop_name))`;
 *   - panel («Coste por cultivo») y la RPC `get_crop_costs`: el texto tal cual.
 * Desde F2 el ciclo puede llevar su código SIEX (`crop_code`), que es la
 * identidad del cultivo. La regla de F4:
 *
 *   - con código → `siex:<código>` («siex:197» es TOMATE, se escriba como se escriba);
 *   - sin código → la expresión de HOY de ese sitio, SIN TOCARLA (`claveDeHoy`).
 *     Un informe sin ningún ciclo con código sale idéntico al de hoy: lo vigila
 *     `cultivoGrupo.test.ts` con instantáneas «sin código = igual que hoy».
 *
 * EL PUENTE DE TRANSICIÓN. Mientras F5 no codifique los ciclos viejos conviven
 * «Tomate» sin código y TOMATE (197) con código (el disparador pone el nombre
 * canónico, `crop_catalog.name_es`, en los que lo llevan). Partirlos daría dos
 * filas «Tomate» en el mismo informe. Así que una fila SIN código cuya clave de
 * hoy es IGUAL a la clave de hoy del nombre de las filas CON código de UN solo
 * cultivo se une a ese grupo. Es lo mismo que pasaba hoy (los dos se llamaban
 * «Tomate» y caían juntos) y nada más: no resuelve sinónimos ni traducciones
 * («Pomodoro» sin código sigue siendo su propio grupo, como hoy), y sin filas
 * con código el puente está vacío y no cambia nada.
 *
 * Los enlaces viejos (`?crop=tomate`) se traducen con `traducir`: si el puente
 * (o el nombre de antes de canonizar, `crop_name_legacy`) une esa clave a un
 * código, pasa a `siex:<código>`; si no, se queda tal cual.
 *
 * PURO: sin red ni React.
 */

export const PREFIJO_SIEX = "siex:";

/** La clave de hoy de un sitio: de un nombre escrito a su clave (null = sin cultivo). */
export type ClaveDeHoy = (nombre: string | null | undefined) => string | null;

/** Lo mínimo de una fila con cultivo (un ciclo, un sector, una línea ya resuelta). */
export interface FilaCultivo {
  /** `crop_code`: el código SIEX, o null/undefined si no lo tiene. */
  code?: number | null;
  /** `crop_name`: el nombre guardado (con código, el canónico). */
  nombre?: string | null;
  /** `crop_name_legacy`: lo escrito antes de canonizar. Solo sirve para traducir enlaces viejos. */
  legado?: string | null;
}

/** ¿Es un código SIEX utilizable? (entero positivo; null, NaN o texto, no). */
export function esCodigoCultivo(code: unknown): code is number {
  return typeof code === "number" && Number.isInteger(code) && code > 0;
}

/** 197 → «siex:197». */
export function claveSiex(code: number): string {
  return `${PREFIJO_SIEX}${code}`;
}

/** «siex:197» → 197; cualquier otra clave (o null) → null. */
export function codigoDeClave(clave: string | null | undefined): number | null {
  if (!clave || !clave.startsWith(PREFIJO_SIEX)) return null;
  const n = Number(clave.slice(PREFIJO_SIEX.length));
  return esCodigoCultivo(n) ? n : null;
}

// ── Las expresiones de hoy, una por sitio (no se tocan) ─────────────────────

/** Informe de costes (`normName`): «  Tomate   Cherry » → «tomate cherry». */
export const claveNombreNormalizado: ClaveDeHoy = (s) => {
  if (s == null) return null;
  const n = String(s).trim().replace(/\s+/g, " ").toLowerCase();
  return n.length ? n : null;
};

/** Campañas (`lower(trim(crop_name))`): «tomate » → «tomate» (los espacios de dentro se quedan). */
export const claveNombreCampanas: ClaveDeHoy = (s) => {
  const n = s?.trim().toLowerCase();
  return n ? n : null;
};

/** Panel y `get_crop_costs`: el texto tal cual («Tomate» y «tomate» son dos). */
export const claveNombreTalCual: ClaveDeHoy = (s) => (s ? String(s) : null);

// ── El agrupador ────────────────────────────────────────────────────────────

export interface AgrupadorCultivo {
  /** La clave de grupo de una fila: `siex:<código>` (propio o del puente) o la clave de hoy. */
  clave(code: number | null | undefined, nombre: string | null | undefined): string | null;
  /** El código del grupo de la fila (el suyo o el del puente), o null si es un grupo por nombre. */
  codigo(code: number | null | undefined, nombre: string | null | undefined): number | null;
  /** Una clave de un enlace viejo → la de hoy si se une a un código; si no (o ya es `siex:`), tal cual. */
  traducir(clave: string): string;
}

/**
 * Prepara el agrupador de UN conjunto de filas (todo lo que el informe tiene
 * cargado, no solo lo filtrado: la clave de una fila no cambia al filtrar).
 */
export function crearAgrupadorCultivo(filas: Iterable<FilaCultivo>, claveDeHoy: ClaveDeHoy): AgrupadorCultivo {
  /* clave de hoy del nombre canónico → códigos con ese nombre (puente). */
  const porNombre = new Map<string, Set<number>>();
  /* clave de hoy de lo escrito antes de canonizar → códigos (solo para traducir enlaces). */
  const porLegado = new Map<string, Set<number>>();
  const apunta = (m: Map<string, Set<number>>, k: string | null, code: number) => {
    if (k == null) return;
    let s = m.get(k);
    if (!s) m.set(k, (s = new Set()));
    s.add(code);
  };
  for (const f of filas) {
    if (!esCodigoCultivo(f.code)) continue;
    apunta(porNombre, claveDeHoy(f.nombre), f.code);
    apunta(porLegado, claveDeHoy(f.legado), f.code);
  }
  const unico = (s: Set<number> | undefined): number | null => (s && s.size === 1 ? [...s][0] : null);
  const puente = new Map<string, number>();
  for (const [k, s] of porNombre) {
    const c = unico(s);
    if (c != null) puente.set(k, c);
  }

  const codigo = (code: number | null | undefined, nombre: string | null | undefined): number | null => {
    if (esCodigoCultivo(code)) return code;
    if (puente.size === 0) return null;
    const k = claveDeHoy(nombre);
    return k == null ? null : puente.get(k) ?? null;
  };

  return {
    codigo,
    clave(code, nombre) {
      const c = codigo(code, nombre);
      return c != null ? claveSiex(c) : claveDeHoy(nombre);
    },
    traducir(clave) {
      if (codigoDeClave(clave) != null) return clave;
      const k = claveDeHoy(clave);
      if (k == null) return clave;
      const c = puente.get(k) ?? unico(porLegado.get(k));
      return c != null ? claveSiex(c) : clave;
    },
  };
}

/** Traduce una lista de claves de filtro (enlaces viejos) sin perder las originales ni repetir. */
export function traducirClaves(claves: readonly string[] | null | undefined, agrupador: AgrupadorCultivo): string[] | undefined {
  if (!claves || claves.length === 0) return claves ? [...claves] : undefined;
  const out: string[] = [];
  for (const k of claves) {
    for (const v of [agrupador.traducir(k), k]) if (!out.includes(v)) out.push(v);
  }
  return out;
}
